import { validateDimensions } from '../core/model';
import { throwIfAborted } from './types';

const MAX_EXIF_BYTES = 1024 * 1024;
const MAX_CHUNKS = 50_000;
const fourCC = (bytes: Uint8Array, offset = 0) => String.fromCharCode(...bytes.subarray(offset, offset + 4));

/** Read only IFD0's Orientation. Never follow thumbnail/SubIFD pointers. */
function exifOrientation(bytes: Uint8Array): { orientation: number; valueOffset?: number; littleEndian: boolean } {
  const start = fourCC(bytes) === 'Exif' && bytes[4] === 0 && bytes[5] === 0 ? 6 : 0;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const fail = () => new Error('Invalid WebP EXIF orientation metadata.');
  if (bytes.length < start + 8) throw fail();
  const littleEndian = bytes[start] === 73 && bytes[start + 1] === 73;
  if (!littleEndian && !(bytes[start] === 77 && bytes[start + 1] === 77)) throw fail();
  if (view.getUint16(start + 2, littleEndian) !== 42) throw fail();
  const offset = view.getUint32(start + 4, littleEndian);
  if (offset === 0) return { orientation: 1, littleEndian };
  const ifd = start + offset;
  if (offset < 8 || ifd + 2 > bytes.length) throw fail();
  const count = view.getUint16(ifd, littleEndian);
  if (ifd + 2 + count * 12 + 4 > bytes.length) throw fail();
  let valueOffset: number | undefined;
  let orientation = 1;
  for (let index = 0; index < count; index++) {
    const entry = ifd + 2 + index * 12;
    if (view.getUint16(entry, littleEndian) !== 0x0112) continue;
    if (valueOffset !== undefined || view.getUint16(entry + 2, littleEndian) !== 3 || view.getUint32(entry + 4, littleEndian) !== 1) throw fail();
    valueOffset = entry + 8; orientation = view.getUint16(valueOffset, littleEndian);
    if (orientation < 1 || orientation > 8) throw fail();
  }
  return { orientation, ...(valueOffset === undefined ? {} : { valueOffset }), littleEndian };
}

/** Keep authored bytes intact. Normalize only a detached decoder input so a
 * browser that begins honoring WebP EXIF cannot rotate the image twice.
 */
export async function prepareWebPOrientation(source: Blob, signal?: AbortSignal): Promise<{ source: Blob; orientation: number }> {
  throwIfAborted(signal);
  let windowStart = 0;
  let window = new Uint8Array(await source.slice(0, 12).arrayBuffer());
  throwIfAborted(signal);
  if (window.length < 12 || fourCC(window) !== 'RIFF' || fourCC(window, 8) !== 'WEBP') return { source, orientation: 1 };
  const end = new DataView(window.buffer).getUint32(4, true) + 8;
  if (end < 20 || end > source.size || end % 2) throw new Error('Invalid WebP container size.');
  const header = async (offset: number): Promise<Uint8Array> => {
    throwIfAborted(signal);
    if (offset < windowStart || offset + 8 > windowStart + window.length) {
      windowStart = offset;
      window = new Uint8Array(await source.slice(offset, Math.min(end, offset + 64 * 1024)).arrayBuffer());
      throwIfAborted(signal);
    }
    return window.subarray(offset - windowStart, offset - windowStart + 8);
  };
  let count = 0; let found = false;
  let result = { source, orientation: 1 };
  for (let offset = 12; offset < end;) {
    if (++count > MAX_CHUNKS) throw new Error('WebP exceeds the chunk inspection limit.');
    if (offset + 8 > end) throw new Error('Truncated WebP chunk header.');
    const bytes = await header(offset);
    const size = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(4, true);
    const data = offset + 8; const next = data + size + (size % 2);
    if (next > end) throw new Error('Truncated WebP chunk.');
    if (fourCC(bytes) === 'EXIF') {
      if (found) throw new Error('Multiple WebP EXIF chunks are ambiguous.');
      found = true;
      if (size > MAX_EXIF_BYTES) throw new Error('WebP EXIF exceeds the 1 MiB inspection limit.');
      const exif = new Uint8Array(await source.slice(data, data + size).arrayBuffer());
      throwIfAborted(signal);
      const { orientation, valueOffset, littleEndian } = exifOrientation(exif);
      if (orientation !== 1 && valueOffset !== undefined) {
        new DataView(exif.buffer).setUint16(valueOffset, 1, littleEndian);
        result = { source: new Blob([source.slice(0, data), exif, source.slice(data + size)], { type: source.type || 'image/webp' }), orientation };
      }
    }
    offset = next;
  }
  return result;
}

/** Explicit WebP orientation; JPEG/PNG keep their browser metadata decoding. */
export async function decodeOrientedImage(source: Blob, options: ImageBitmapOptions, signal?: AbortSignal, allowOptionlessFallback = false): Promise<ImageBitmap> {
  const prepared = await prepareWebPOrientation(source, signal);
  let bitmap: ImageBitmap | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    try { bitmap = await createImageBitmap(prepared.source, options); }
    catch (error) {
      throwIfAborted(signal);
      if (!allowOptionlessFallback) throw error;
      bitmap = await createImageBitmap(prepared.source);
    }
    throwIfAborted(signal);
    validateDimensions(bitmap.width, bitmap.height);
    if (prepared.orientation === 1) { const result = bitmap; bitmap = undefined; return result; }
    const width = bitmap.width; const height = bitmap.height;
    canvas = document.createElement('canvas');
    canvas.width = prepared.orientation >= 5 ? height : width;
    canvas.height = prepared.orientation >= 5 ? width : height;
    const context = canvas.getContext('2d', { colorSpace: 'srgb' });
    if (!context) throw new Error('Canvas 2D is unavailable for WebP orientation.');
    const transforms: Record<number, [number, number, number, number, number, number]> = {
      2: [-1, 0, 0, 1, width, 0], 3: [-1, 0, 0, -1, width, height], 4: [1, 0, 0, -1, 0, height],
      5: [0, 1, 1, 0, 0, 0], 6: [0, 1, -1, 0, height, 0], 7: [0, -1, -1, 0, height, width], 8: [0, -1, 1, 0, 0, width],
    };
    context.setTransform(...transforms[prepared.orientation]!); context.imageSmoothingEnabled = false;
    context.drawImage(bitmap, 0, 0); bitmap.close(); bitmap = undefined;
    bitmap = await createImageBitmap(canvas);
    throwIfAborted(signal);
    const result = bitmap; bitmap = undefined; return result;
  } finally {
    bitmap?.close();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
